<template>
    <div>
        <v-card :loading="loading">
            <v-card-title>
                <v-btn small icon @click="close" color="primary" class="mr-2">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
                <h3>New Item</h3>
            </v-card-title>

            <v-divider />

            <v-card-text>
                <v-form ref="form" v-model="valid">
                    <v-row>
                        <v-col cols="12" v-if="!selectedMaterialObj">
                            <v-combobox
                                v-model="selectedMaterial"
                                :items="materials"
                                item-text="name"
                                item-value="id"
                                label="Material"
                                prepend-icon="mdi-cube"
                                :rules="[rules.required]"
                                @change="onMaterialChange"
                                clearable
                                placeholder="Type to search materials..."
                            />
                        </v-col>

                        <template v-else>
                            <v-col cols="12">
                                <!-- Material and Attributes Row -->
                                <v-row class="mb-4">
                                    <!-- Material Selector -->
                                    <v-col cols="12" sm="6" md="3">
                                        <v-combobox
                                            v-model="selectedMaterial"
                                            :items="materials"
                                            item-text="name"
                                            item-value="id"
                                            label="Material*"
                                            prepend-icon="mdi-cube"
                                            :rules="[rules.required]"
                                            @change="onMaterialChange"
                                            clearable
                                            placeholder="Type to search materials..."
                                        />
                                    </v-col>

                                    <!-- Others: Simple Description Field -->
                                    <template
                                        v-if="selectedMaterialObj.id === '200'"
                                    >
                                        <v-col cols="12" md="9">
                                            <v-text-field
                                                v-model="customDescription"
                                                label="Description*"
                                                prepend-icon="mdi-text"
                                                :rules="[rules.required]"
                                                @input="updateCustomDescription"
                                                placeholder="Enter item description..."
                                            />
                                        </v-col>
                                    </template>

                                    <!-- Material Attributes (not shown for Others) -->
                                    <template
                                        v-if="selectedMaterialObj.id !== '200'"
                                    >
                                        <v-col
                                            v-for="attr in selectedMaterialObj.attributes ||
                                                []"
                                            :key="attr.name"
                                            cols="12"
                                            sm="6"
                                            md="3"
                                        >
                                            <v-autocomplete
                                                :label="attr.name + '*'"
                                                :items="
                                                    getAttributeOptions(attr)
                                                "
                                                v-model="
                                                    selectedAttributes[
                                                        attr.name
                                                    ]
                                                "
                                                item-text="label"
                                                item-value="code"
                                                :rules="[rules.required]"
                                                @change="
                                                    onAttributeChange(attr)
                                                "
                                            />
                                        </v-col>
                                    </template>

                                    <!-- Extrusion Selection and Attributes -->
                                    <template
                                        v-if="
                                            createdFromExtrusion &&
                                                extrusions.length > 0
                                        "
                                    >
                                        <!-- Extrusion Selector -->
                                        <v-col cols="12" sm="6" md="3">
                                            <v-combobox
                                                v-model="selectedExtrusion"
                                                :items="extrusions"
                                                item-text="displayName"
                                                item-value="matriz"
                                                label="Die Code*"
                                                prepend-icon="mdi-cube"
                                                :rules="[rules.required]"
                                                @change="onExtrusionChange"
                                                clearable
                                                no-data-text="No extrusions found"
                                                placeholder="Type to search extrusions..."
                                            />
                                        </v-col>

                                        <!-- Extrusion Attributes -->
                                        <template v-if="selectedExtrusionObj">
                                            <!-- Alloy -->
                                            <v-col cols="12" sm="6" md="3">
                                                <v-text-field
                                                    v-model="alloyValue"
                                                    label="Alloy"
                                                    :value="alloyValue"
                                                    prepend-icon="mdi-crystal-ball"
                                                    @input="updateDescription()"
                                                    :rules="[rules.required]"
                                                />
                                            </v-col>

                                            <!-- Temper -->
                                            <v-col cols="12" sm="6" md="3">
                                                <v-text-field
                                                    v-model="temperValue"
                                                    label="Temper"
                                                    :value="temperValue"
                                                    prepend-icon="mdi-fire"
                                                    @input="updateDescription()"
                                                    :rules="[rules.required]"
                                                />
                                            </v-col>

                                            <!-- Long -->
                                            <v-col cols="12" sm="6" md="3">
                                                <v-text-field
                                                    label="Long*"
                                                    v-model="
                                                        selectedExtrusionAttributes.long
                                                    "
                                                    placeholder="Enter long"
                                                    :rules="[rules.required]"
                                                    @input="updateDescription"
                                                    prepend-icon="mdi-arrow-right-bold"
                                                    suffix="mm"
                                                />
                                            </v-col>

                                            <!-- Width -->
                                            <v-col cols="12" sm="6" md="3">
                                                <v-text-field
                                                    label="Width"
                                                    :value="
                                                        selectedExtrusionObj.width ||
                                                            'N/A'
                                                    "
                                                    disabled
                                                    prepend-icon="mdi-arrow-right"
                                                    suffix="in"
                                                />
                                            </v-col>

                                            <!-- Height -->
                                            <v-col cols="12" sm="6" md="3">
                                                <v-text-field
                                                    label="Height"
                                                    :value="
                                                        selectedExtrusionObj.height ||
                                                            'N/A'
                                                    "
                                                    disabled
                                                    prepend-icon="mdi-arrow-up"
                                                    suffix="in"
                                                />
                                            </v-col>

                                            <!-- Thickness -->
                                            <v-col cols="12" sm="6" md="3">
                                                <v-text-field
                                                    label="Thickness"
                                                    :value="
                                                        selectedExtrusionObj.thickness ||
                                                            'N/A'
                                                    "
                                                    disabled
                                                    prepend-icon="mdi-arrow-expand-vertical"
                                                    suffix="in"
                                                />
                                            </v-col>
                                            <v-col
                                                v-for="attr in filteredExtrusionAttributes"
                                                :key="'extrusion-' + attr.name"
                                                cols="12"
                                                sm="6"
                                                md="3"
                                            >
                                                <v-text-field
                                                    :label="attr.name + '*'"
                                                    v-model="
                                                        selectedExtrusionAttributes[
                                                            attr.name
                                                        ]
                                                    "
                                                    :placeholder="
                                                        `Enter ${attr.name.toLowerCase()}`
                                                    "
                                                    :hint="
                                                        `Default: ${attr
                                                            .options[0]
                                                            ?.label || 'N/A'}`
                                                    "
                                                    persistent-hint
                                                    :rules="[rules.required]"
                                                    @input="updateDescription"
                                                />
                                            </v-col>
                                        </template>
                                    </template>
                                   <!-- UC Code (only shown for Extrusion material) -->
                                    <v-col
                                        cols="12"
                                        sm="6"
                                        md="3"
                                        v-if="selectedMaterialObj.id === '1'"
                                    >
                                        <v-autocomplete
                                            :label="'UC CODE'"
                                            prepend-icon="mdi-barcode"
                                            :items="UcCodes"
                                            item-text="longName"
                                            item-value="id"
                                            v-model="selectedUcCode"
                                            @change="updateDescription"
                                            :rules="[rules.requiredIfExtrusion]"
                                        />
                                    </v-col>
                                </v-row>

                                <!-- Description and Long Name (not shown for Others) -->
                                <template
                                    v-if="selectedMaterialObj.id !== '200'"
                                >
                                    <v-row class="mb-0 pb-0">
                                        <v-col cols="12" md="12">
                                            <v-text-field
                                                :value="item.longName"
                                                label="Long Description (auto-generated) - PA"
                                                prepend-icon="mdi-text"
                                                disabled
                                            />
                                        </v-col>
                                    </v-row>
                                    <v-row class="mb-4 pt-0">
                                        <v-col cols="12" md="8">
                                            <v-text-field
                                                :value="item.description"
                                                label="Short Description (auto-generated) - SAP"
                                                prepend-icon="mdi-book-open-outline"
                                                disabled
                                            />
                                        </v-col>
                                        <!-- Visit URL Button -->
                                        <v-col
                                            cols="12"
                                            md="4"
                                            class="d-flex align-center"
                                        >
                                            <v-btn
                                                color="primary"
                                                outlined
                                                block
                                                @click="
                                                    openMaterialUrl(
                                                        selectedMaterialObj.url
                                                    )
                                                "
                                                :disabled="
                                                    !selectedMaterialObj.url ||
                                                        !isValidUrl(
                                                            selectedMaterialObj.url
                                                        )
                                                "
                                            >
                                                <v-icon left
                                                    >mdi-open-in-new</v-icon
                                                >
                                                Visit URL
                                            </v-btn>
                                        </v-col>
                                    </v-row>
                                </template>
                            </v-col>
                        </template>
                    </v-row>
                </v-form>
            </v-card-text>

            <v-card-actions>
                <v-spacer />
                <v-btn
                    color="primary"
                    :disabled="!valid"
                    @click="save"
                    :loading="loading"
                >
                    REQUEST
                </v-btn>
            </v-card-actions>
        </v-card>

        <v-dialog v-model="newOptionDialog" max-width="500px" persistent>
            <v-card>
                <v-card-title>Create New Option</v-card-title>
                <v-card-text>
                    <v-text-field
                        v-model="newOptionForm.label"
                        label="Option label"
                        :rules="[rules.required]"
                    />
                    <v-text-field
                        v-model="newOptionForm.code"
                        label="Option code"
                        :rules="[rules.required]"
                    />
                </v-card-text>
                <v-card-actions>
                    <v-btn text @click="closeNewOptionDialog">Close</v-btn>
                    <v-spacer />
                    <v-btn
                        color="primary"
                        :loading="creatingNewOption"
                        :disabled="
                            creatingNewOption ||
                                !newOptionForm.label ||
                                !newOptionForm.code
                        "
                        @click="createNewOption"
                    >
                        Create
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import API from '@/services/api'
import { mapMutations } from 'vuex'

export default {
    name: 'NewItem',
    props: {
        materials: {
            type: Array,
            default: () => [],
        },
        extrusions: {
            type: Array,
            default: () => [],
        },
        UcCodes: {
            type: Array,
            default: () => [],
        },
    },
    data: () => ({
        valid: false,
        loading: false,
        selectedMaterial: null,
        selectedMaterialObj: null,
        selectedAttributes: {},
        selectedExtrusion: null,
        selectedExtrusionObj: null,
        selectedExtrusionAttributes: {},
        selectedUcCode: null,
        item: {
            description: '',
            longName: '',
        },
        createdFromExtrusion: false,
        alloyValue: '',
        temperValue: '',
        onlyCode: null,
        customDescription: '',
        newOptionDialog: false,
        creatingNewOption: false,
        newOptionForm: {
            label: '',
            code: '',
        },
        selectedAttrForNewOption: null,
        NEW_OPTION_CODE: '__NEW_OPTION__',
    }),
    computed: {
        rules() {
            return {
                required: v => !!v || 'The value is required!',
                requiredIfExtrusion: v => {
                    if (this.createdFromExtrusion) {
                        return !!v || 'The value is required!'
                    }
                    return true
                },
            }
        },
        filteredExtrusionAttributes() {
            if (!this.selectedExtrusionObj) {
                return []
            }
            return []
        },
    },
    mounted() {
        this.UcCodes.forEach(uc => {
            const name = typeof uc.name === 'string' ? uc.name : (uc.name?.toString() || '')
            const description = typeof uc.description === 'string' ? uc.description : (uc.description?.toString() || '')
            uc.longName = description ? `${name} (${description})` : name
        })
        this.materials.push({
            id: '200',
            name: 'OTHERS',
            abbreviation: '',
            attributes: [],
        })
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        getAttributeOptions(attr) {
            const options = Array.isArray(attr?.options)
                ? [...attr.options]
                : []

            if (attr?.new) {
                const hasNewOption = options.some(
                    opt => opt?.code === this.NEW_OPTION_CODE
                )

                if (!hasNewOption) {
                    options.push({
                        label: 'New',
                        code: this.NEW_OPTION_CODE,
                    })
                }
            }

            return options
        },
        onAttributeChange(attr) {
            const selectedCode = this.selectedAttributes[attr.name]
            if (selectedCode === this.NEW_OPTION_CODE) {
                this.selectedAttrForNewOption = attr
                this.newOptionForm = {
                    label: '',
                    code: '',
                }
                this.newOptionDialog = true
                return
            }
            this.updateDescription()
        },
        closeNewOptionDialog() {
            if (this.selectedAttrForNewOption) {
                this.selectedAttributes[
                    this.selectedAttrForNewOption.name
                ] = null
            }
            this.newOptionDialog = false
            this.creatingNewOption = false
            this.selectedAttrForNewOption = null
            this.newOptionForm = {
                label: '',
                code: '',
            }
            this.updateDescription()
        },
        generateId() {
            return `${Date.now()}_${Math.random()
                .toString(36)
                .slice(2, 10)}`
        },
        mapMaterialForUI(material) {
            const attributesSource = material?.attributes || {}
            const attributesArray = Array.isArray(attributesSource)
                ? attributesSource
                : Object.keys(attributesSource).map(attrId => {
                      const attr = attributesSource[attrId] || {}
                      const optionsSource = attr.options || {}
                      const optionsArray = Array.isArray(optionsSource)
                          ? optionsSource
                          : Object.keys(optionsSource).map(optId => ({
                                id: optId,
                                ...optionsSource[optId],
                            }))

                      return {
                          id: attrId,
                          ...attr,
                          options: optionsArray,
                      }
                  })

            return {
                ...material,
                attributes: attributesArray,
            }
        },
        buildMaterialPayload(material) {
            const attributesObj = {}
            const attributesArray = Array.isArray(material?.attributes)
                ? material.attributes
                : []

            attributesArray.forEach(attr => {
                const attrId = attr.id || this.generateId()
                const optionsObj = {}
                const optionsArray = Array.isArray(attr.options)
                    ? attr.options
                    : []

                optionsArray.forEach(opt => {
                    if (!opt || opt.code === this.NEW_OPTION_CODE) return
                    const optId = opt.id || this.generateId()
                    optionsObj[optId] = {
                        id: optId,
                        label: opt.label,
                        code: opt.code,
                    }
                })

                attributesObj[attrId] = {
                    id: attrId,
                    name: attr.name,
                    abbreviation: attr.abbreviation || '',
                    new: Boolean(attr.new),
                    options: optionsObj,
                }
            })

            // Only send keys accepted by settings/materials Joi schema.
            return {
                id: material.id,
                name: material.name,
                description: material.description || '',
                url: material.url || '',
                abbreviation: material.abbreviation,
                createdOn: material.createdOn,
                updatedOn: material.updatedOn,
                attributes: attributesObj,
            }
        },
        async createNewOption() {
            if (
                !this.selectedMaterialObj ||
                !this.selectedAttrForNewOption ||
                !this.newOptionForm.label ||
                !this.newOptionForm.code
            ) {
                return
            }

            try {
                this.creatingNewOption = true

                const newLabel = this.newOptionForm.label.trim()
                const newCode = this.newOptionForm.code.trim()

                const materialClone = JSON.parse(
                    JSON.stringify(this.selectedMaterialObj)
                )
                const attr = (materialClone.attributes || []).find(
                    a => a.id === this.selectedAttrForNewOption.id
                )

                if (!attr) {
                    throw new Error('Attribute not found')
                }

                const duplicated = (attr.options || []).some(
                    opt =>
                        String(opt.code || '').toLowerCase() ===
                        newCode.toLowerCase()
                )
                if (duplicated) {
                    throw new Error(
                        'Option code already exists for this attribute'
                    )
                }

                if (!Array.isArray(attr.options)) {
                    attr.options = []
                }
                attr.options.push({
                    id: this.generateId(),
                    label: newLabel,
                    code: newCode,
                })

                const materialPayload = this.buildMaterialPayload(materialClone)
                const response = await API.updateMaterial(materialPayload)
                const updatedMaterial = this.mapMaterialForUI(
                    response?.data?.material || materialPayload
                )

                const materialIndex = this.materials.findIndex(
                    m => m.id === updatedMaterial.id
                )
                if (materialIndex !== -1) {
                    this.$set(this.materials, materialIndex, updatedMaterial)
                }
                this.selectedMaterialObj = updatedMaterial
                this.selectedAttributes[
                    this.selectedAttrForNewOption.name
                ] = newCode

                this.newOptionDialog = false
                this.selectedAttrForNewOption = null
                this.newOptionForm = {
                    label: '',
                    code: '',
                }
                this.updateDescription()
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.creatingNewOption = false
            }
        },
        onMaterialChange(material) {
            // If material is cleared (null or empty), reset everything
            if (!material || !material.id) {
                this.selectedMaterialObj = null
                this.createdFromExtrusion = false
                this.selectedAttributes = {}
                this.selectedExtrusionAttributes = {}
                this.selectedExtrusion = null
                this.selectedExtrusionObj = null
                this.selectedUcCode = null
                this.item.description = ''
                this.item.longName = ''
                this.customDescription = ''
                return
            }

            this.selectedMaterialObj = this.materials.find(
                m => m.id === material.id
            )

            this.createdFromExtrusion = material.id === '1'

            this.selectedAttributes = {}
            this.selectedExtrusionAttributes = {}
            this.selectedExtrusion = null
            this.selectedExtrusionObj = null
            this.selectedUcCode = null
            this.item.description = ''
            this.item.longName = ''
            this.customDescription = ''

            // For "Others" material, no need to call updateDescription
            if (material.id !== '200') {
                this.updateDescription()
            }
        },
        onExtrusionChange(extrusionMatriz) {
            // If extrusion is cleared (null or empty), reset extrusion-related fields
            if (!extrusionMatriz || !extrusionMatriz.matriz) {
                this.selectedExtrusionObj = null
                this.selectedExtrusionAttributes = {}
                this.updateDescription()
                return
            }
            this.selectedExtrusionObj = this.extrusions.find(
                e => e.matriz === extrusionMatriz.matriz
            )

            // Preload values from JSON as defaults
            this.selectedExtrusionAttributes = {}
            this.alloyValue = this.selectedExtrusionObj.alloy || 'N/A'
            this.temperValue = this.selectedExtrusionObj.temper || 'N/A'
            this.updateDescription()
        },
        updateDescription() {
            if (!this.selectedMaterialObj) return

            // Check if "Extrusion" material is selected
            if (this.selectedMaterialObj.id === '1') {
                // For Extrusion material, generate description based on extrusion selection
                if (this.selectedExtrusionObj) {
                    // Generate CUT format: CUT{matriz} {alloy}{temper}x{long} UC{UcCode} {medida1}x{medida2}x{espesor1}
                    let cuCode =
                        this.selectedExtrusionObj.abreviatura +
                        ' ' +
                        this.selectedExtrusionObj.matriz +
                        ' '

                    // Add alloy and temper
                    if (this.alloyValue && this.alloyValue.trim()) {
                        cuCode += this.alloyValue.trim()
                    }
                    if (this.temperValue && this.temperValue.trim()) {
                        cuCode += this.temperValue.trim()
                    }

                    // Add long from user input
                    const longValue = this.selectedExtrusionAttributes['long']
                    if (longValue && longValue?.trim()) {
                        cuCode += 'x' + longValue?.trim()
                    }

                    // Add UC code if selected
                    if (this.selectedUcCode) {
                        const selectedUc = this.UcCodes.find(
                            uc => uc.id === this.selectedUcCode
                        )
                        if (selectedUc && selectedUc.consecutive) {
                            cuCode += ' ' + selectedUc.consecutive
                        }
                    }

                    // Truncate to 40 characters for description, keep full for longName
                    this.item.description =
                        cuCode.length > 40 ? cuCode.substring(0, 40) : cuCode
                    this.item.longName = cuCode
                } else {
                    this.item.description = '' // Start with "CU" if no extrusion selected
                    this.item.longName = ''
                }
            } else {
                // For regular materials, generate description with abbreviations
                let nameParts = [this.selectedMaterialObj.abbreviation]
                if (this.selectedMaterialObj.attributes) {
                    this.selectedMaterialObj.attributes.forEach(attr => {
                        const code = this.selectedAttributes[attr.name]
                        if (code) nameParts.push(code)
                    })
                }

                // Add extrusion code and attributes if createdFromExtrusion
                if (this.createdFromExtrusion && this.selectedExtrusionObj) {
                    // Generate CUT format: CUT{matriz} {alloy}{temper}x{long} UC{UcCode} {medida1}x{medida2}x{espesor1}
                    let cuCode = this.selectedExtrusionObj.abreviatura + ' '
                    this.selectedExtrusionObj.matriz + ' '

                    // Add alloy and temper
                    if (this.alloyValue && this.alloyValue.trim()) {
                        cuCode += this.alloyValue.trim()
                    }
                    if (this.temperValue && this.temperValue.trim()) {
                        cuCode += this.temperValue.trim()
                    }

                    // Add long from user input
                    const longValue = this.selectedExtrusionAttributes['long']
                    if (longValue && longValue?.trim()) {
                        cuCode += 'x' + longValue?.trim()
                    }

                    // Add UC code if selected
                    if (this.selectedUcCode) {
                        const selectedUc = this.UcCodes.find(
                            uc => uc.id === this.selectedUcCode
                        )
                        if (selectedUc && selectedUc.consecutive) {
                            cuCode += ' ' + selectedUc.consecutive
                        }
                    }

                    // Add dimensions: {medida1}x{medida2}x{espesor1}

                    nameParts.push(cuCode)
                }

                const fullDescription = nameParts.join('')
                // Truncate to 40 characters for description, keep full for longName
                this.item.description =
                    fullDescription.length > 40
                        ? fullDescription.substring(0, 40)
                        : fullDescription
                this.item.longName = fullDescription
            }

            // Always update longName for both extrusion and regular materials
            this.updateLongName()
        },

        updateLongName() {
            if (!this.selectedMaterialObj) return

            // Check if "Extrusion" material is selected
            if (this.selectedMaterialObj.id === '1') {
                if (this.selectedExtrusionObj) {
                    // Generate longName format: CUT{matriz} {alloy}{temper}x{long} {medida1}x{medida2}x{espesor1}
                    let longName =
                        this.selectedExtrusionObj.abreviatura +
                        ' ' +
                        this.selectedExtrusionObj.matriz +
                        ' '

                    // Add alloy and temper
                    if (this.alloyValue && this.alloyValue.trim()) {
                        longName += this.alloyValue.trim()
                    }
                    if (this.temperValue && this.temperValue.trim()) {
                        longName += this.temperValue.trim()
                    }

                    // Add long from user input
                    const longValue = this.selectedExtrusionAttributes['long']
                    if (longValue && longValue.trim()) {
                        longName += 'x' + longValue.trim()
                    }

                    // Add UC code if selected
                    if (this.selectedUcCode) {
                        const selectedUc = this.UcCodes.find(
                            uc => uc.id === this.selectedUcCode
                        )
                        if (selectedUc && selectedUc.consecutive) {
                            longName += ' ' + selectedUc.consecutive
                        }
                    }
                    // Add dimensions: {medida1}x{medida2}x{espesor1}
                    const dimensions = []
                    if (this.selectedExtrusionObj.width)
                        dimensions.push(this.selectedExtrusionObj.width)
                    if (this.selectedExtrusionObj.height)
                        dimensions.push(this.selectedExtrusionObj.height)
                    if (this.selectedExtrusionObj.thickness)
                        dimensions.push(this.selectedExtrusionObj.thickness)

                    if (dimensions.length > 0) {
                        longName += ' ' + dimensions.join('x')
                    }

                    this.item.longName = longName
                } else {
                    this.item.longName = ''
                }
                return
            } else {
                // For regular materials, generate longName with full names
                let longNameParts = [this.selectedMaterialObj.name]
                if (this.selectedMaterialObj.attributes) {
                    this.selectedMaterialObj.attributes.forEach(attr => {
                        const code = this.selectedAttributes[attr.name]
                        if (code) {
                            const selectedOption = this.getAttributeOptions(
                                attr
                            ).find(option => option.code === code)
                            if (selectedOption) {
                                longNameParts.push(selectedOption.label)
                            }
                        }
                    })
                }

                // Add extrusion code and attributes to longName if createdFromExtrusion
                if (this.createdFromExtrusion && this.selectedExtrusionObj) {
                    longNameParts.push(
                        `Extrusion ${this.selectedExtrusionObj.matriz}`
                    )

                    if (this.selectedExtrusionObj.attributes) {
                        this.selectedExtrusionObj.attributes.forEach(attr => {
                            const value = this.selectedExtrusionAttributes[
                                attr.name
                            ]
                            if (value && value.trim()) {
                                longNameParts.push(value.trim())
                            }
                        })
                    }
                }

                this.item.longName = longNameParts.join(' ')
            }
        },

        updateCustomDescription() {
            // For "Others" material, set both description and longName to the custom input
            this.item.description = this.customDescription
            this.item.longName = this.customDescription
        },

        async save() {
            if (!this.selectedMaterialObj) return

            // Check if "Others" material is selected
            if (this.selectedMaterialObj.id === '200') {
                // For "Others" material, just validate the custom description
                if (!this.customDescription || !this.customDescription.trim()) {
                    this.setErrorItems({
                        source: this.$options.name,
                        message: 'Please enter a description.',
                    })
                    return
                }

                try {
                    this.loading = true
                    await API.addRequest({
                        description: this.item.description,
                        longName: this.item.longName,
                        ucCode: null, // ucCode is null for Others
                    })
                    this.$emit('save')
                } catch (error) {
                    this.setErrorItems({
                        source: this.$options.name,
                        message: error.message,
                    })
                } finally {
                    this.loading = false
                }
                return
            }

            // Check if "Extrusion" material is selected
            if (this.selectedMaterialObj.id === '1') {
                // For Extrusion material, validate extrusion selection and attributes
                if (!this.selectedExtrusionObj) {
                    this.setErrorItems({
                        source: this.$options.name,
                        message: 'Please select an extrusion.',
                    })
                    return
                }

                const allExtrusionAttributesSelected = this.selectedExtrusionObj
                    .attributes
                    ? this.selectedExtrusionObj.attributes.every(
                          attr => !!this.selectedExtrusionAttributes[attr.name]
                      )
                    : true
                if (!allExtrusionAttributesSelected) {
                    this.setErrorItems({
                        source: this.$options.name,
                        message:
                            'Select an option for every extrusion attribute.',
                    })
                    return
                }
            } else {
                // For regular materials, validate material attributes
                const allSelected = this.selectedMaterialObj.attributes
                    ? this.selectedMaterialObj.attributes.every(
                          attr => !!this.selectedAttributes[attr.name]
                      )
                    : true
                if (!allSelected) {
                    this.setErrorItems({
                        source: this.$options.name,
                        message:
                            'Select an option for every material attribute.',
                    })
                    return
                }

                // Validate extrusion selection and attributes if createdFromExtrusion
                if (this.createdFromExtrusion) {
                    if (!this.selectedExtrusionObj) {
                        this.setErrorItems({
                            source: this.$options.name,
                            message: 'Please select an extrusion.',
                        })
                        return
                    }

                    const allExtrusionAttributesSelected = this
                        .selectedExtrusionObj.attributes
                        ? this.selectedExtrusionObj.attributes.every(
                              attr =>
                                  !!this.selectedExtrusionAttributes[attr.name]
                          )
                        : true
                    if (!allExtrusionAttributesSelected) {
                        this.setErrorItems({
                            source: this.$options.name,
                            message:
                                'Select an option for every extrusion attribute.',
                        })
                        return
                    }
                }
            }
            try {
                this.loading = true
                await API.addRequest({
                    description: this.item.description,
                    longName: this.item.longName,
                    ucCode: this.selectedUcCode,
                })
                this.$emit('save')
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        close() {
            this.$emit('closeDialog')
        },
        openMaterialUrl(url) {
            if (this.isValidUrl(url)) {
                window.open(url, '_blank')
            }
        },
        isValidUrl(string) {
            try {
                new URL(string)
                return true
            } catch (_) {
                return false
            }
        },
    },
}
</script>
